<template>
    <section class="mb-16 sm:mb-20">
        <div class="max-w-screen-xl mx-auto px-4">
            <!-- Icon & Heading -->
            <div class="text-center">
                <div class="max-w-max mx-auto relative mb-6">
                    <NuxtImg src="/images/circuit.png" alt="Circuit Board" width="298" height="179"
                         loading="lazy" />
                    <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 
            inline-flex items-center justify-center 
            w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 
            rounded-2xl bg-[image:var(--primary-gradient)]">
                        <!-- <NuxtImg src="/images/shopping-bag.png" alt="Shopping Bag"
                            class="w-6 sm:w-8 md:w-9 lg:w-[36px] h-auto" loading="lazy" /> -->
                            <NsLogo name="NsShoppingBagIcon" class="w-6 sm:w-8 md:w-9 lg:w-[36px] h-auto"/>
                    </div>
                </div>

                <!-- Heading -->
                <h2
                    class="text-xl sm:text-2xl md:text-3xl lg:text-4xl text-[var(--text-secondary)] font-bold -mt-4 sm:-mt-6 mb-3 sm:mb-4">
                    Elevate the Shopping Experience
                </h2>
                <p
                    class="text-sm sm:text-base md:text-lg lg:text-xl text-[var(--text-gray)] max-w-2xl mx-auto mb-10 sm:mb-12">
                    Our frontend maximizes the impact of customer-facing features,
                    designed for convenience and engagement.
                </p>
            </div>

            <!-- Row 1 (2 side by side on md+, stacked on mobile) -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 lg:gap-10 mb-10 sm:mb-12">
                <Card v-for="(item, index) in features.slice(0, 2)" :key="'row1-' + index" unstyled class="rounded-2xl">
                    <template #content>
                        <NuxtImg :src="item.image" :alt="item.title" width="604" height="375" class="rounded-xl w-full mb-3 sm:mb-4 h-auto" loading="lazy" />
                        <h3 class="text-lg sm:text-xl md:text-2xl text-[var(--text-secondary)] font-semibold mb-2">
                            {{ item.title }}
                        </h3>
                        <p class="text-sm sm:text-base md:text-lg lg:text-xl text-[var(--text-gray)]">
                            {{ item.description }}
                        </p>
                    </template>
                </Card>
            </div>

            <!-- Row 2 (single full width) -->
            <div class="mb-10 sm:mb-12">
                <Card unstyled class="rounded-2xl">
                    <template #content>
                        <NuxtImg :src="features[2]?.image" :alt="features[2]?.title"
                           width="1248" height="448" class="rounded-xl w-full mb-3 sm:mb-4 h-auto" loading="lazy" />
                        <h3 class="text-lg sm:text-xl md:text-2xl text-[var(--text-secondary)] font-semibold mb-2">
                            {{ features[2]?.title }}
                        </h3>
                        <p class="text-sm sm:text-base md:text-lg lg:text-xl text-[var(--text-gray)]">
                            {{ features[2]?.description }}
                        </p>
                    </template>
                </Card>
            </div>

            <!-- Row 3 (2 side by side on md+, stacked on mobile) -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 lg:gap-10">
                <Card v-for="(item, index) in features.slice(3, 5)" :key="'row3-' + index" unstyled class="rounded-2xl">
                    <template #content>
                        <NuxtImg :src="item.image" :alt="item.title" width="604" height="375" class="rounded-xl w-full mb-3 sm:mb-4 h-auto" loading="lazy" />
                        <h3 class="text-lg sm:text-xl md:text-2xl text-[var(--text-secondary)] font-semibold mb-2">
                            {{ item.title }}
                        </h3>
                        <p class="text-sm sm:text-base md:text-lg lg:text-xl text-[var(--text-gray)]">
                            {{ item.description }}
                        </p>
                    </template>
                </Card>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
const NsLogo = defineAsyncComponent(() => import("./NsLogo.vue"));

const features = [
    {
        image: "/images/multi-vendor-marketplace.png",
        title: "Personalized Multi-Vendor Marketplace",
        description:
            "Discover products from various vendors, seamlessly presented in a single, unified storefront.",
    },
    {
        image: "/images/global-shopping-experience.png",
        title: "Global Shopping Experience",
        description:
            "Automatically adapt language and currency settings based on customer location for a truly international shopping experience.",
    },
    {
        image: "/images/immersive-ar.png",
        title: "Immersive AR Product Views",
        description:
            "Engage customers with 3D product visualization and AR features for a more interactive and realistic shopping experience.",
    },
    {
        image: "/images/smart-abandoned.png",
        title: "Smart Abandoned Cart Recovery",
        description:
            "Increase conversion rates with automated reminders and discounts for customers who left items in their cart.",
    },
    {
        image: "/images/dynamic-brand.png",
        title: "Dynamic Brand Store Pages",
        description:
            "Showcase individual brands with dedicated, customizable pages that highlight their unique products and stories.",
    },
];
</script>
